import { createDefaultMatchRows, normalizeAttitude } from './teamSpiritUtils'
import {
  calendarAnchorDate,
  calendarAnchorPosition,
  u21CalendarAnchorDate,
  u21CalendarAnchorPosition,
  dayLabels,
  daysPerPart,
  daysPerSeason,
  daysPerWeek,
  defaultCountryValues,
  defaultOpponentCountries,
} from './teamSpiritConstants'
import { matchSchedule as externalMatchSchedule } from './data/teamSpiritMatchSchedule'
import { worldCupCalendar } from './data/worldCupCalendar'

export const pad2 = value => String(value).padStart(2, '0')

const toUtcNoon = date =>
  new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0)
  )

const getCalendarPositionFromAnchor = (
  targetDate,
  anchorDateValue,
  anchorPosition
) => {
  const anchorDate = toUtcNoon(anchorDateValue)
  const nowDate = toUtcNoon(targetDate)
  const dayDiff = Math.floor((nowDate - anchorDate) / (24 * 60 * 60 * 1000))
  const anchorDayIndex =
    (anchorPosition.part - 1) * daysPerPart +
    (anchorPosition.week - 1) * daysPerWeek +
    (anchorPosition.day - 1)
  const absoluteDayIndex = anchorDayIndex + dayDiff
  const seasonShift = Math.floor(absoluteDayIndex / daysPerSeason)
  const normalizedSeasonDayIndex =
    ((absoluteDayIndex % daysPerSeason) + daysPerSeason) % daysPerSeason
  const part = Math.floor(normalizedSeasonDayIndex / daysPerPart) + 1
  const dayIndexInPart = normalizedSeasonDayIndex % daysPerPart
  const week = Math.floor(dayIndexInPart / daysPerWeek) + 1
  const day = (dayIndexInPart % daysPerWeek) + 1

  return {
    nationalSeason: anchorPosition.nationalSeason + seasonShift,
    part,
    week,
    day,
  }
}

export const getCalendarPositionForDate = targetDate =>
  getCalendarPositionFromAnchor(
    targetDate,
    calendarAnchorDate,
    calendarAnchorPosition
  )

export const getCalendarPositionForU21Date = targetDate =>
  getCalendarPositionFromAnchor(
    targetDate,
    u21CalendarAnchorDate,
    u21CalendarAnchorPosition
  )

export const getCurrentCalendarPosition = () =>
  getCalendarPositionForDate(new Date())

export const getCurrentCalendarPositionForNationalTeam = (
  nationalTeamId,
  isU21 = false
) => {
  const current = getCurrentCalendarPosition()
  return Number.isFinite(Number(nationalTeamId)) && isU21
    ? getCalendarPositionForU21Date(new Date())
    : current
}
export const normalizeCountryValues = (countryValues, requiredCountries = []) => {
  const normalized = {}
  requiredCountries.forEach(country => {
    const value = countryValues?.[country] || defaultCountryValues
    normalized[country] = {
      ...value,
      attitude: normalizeAttitude(value.attitude),
      midfieldInput: String(value.midfieldInput ?? ''),
      formation: String(value.formation ?? ''),
      tactic: String(value.tactic ?? ''),
      manualSpiritBeforeInput: String(value.manualSpiritBeforeInput ?? ''),
      midfieldAnchor: Boolean(value.midfieldAnchor),
    }
  })
  return normalized
}

export const toDateKey = value => {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`
  }
  const raw = String(value).trim()
  const isoLikeDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoLikeDate) return `${isoLikeDate[1]}-${isoLikeDate[2]}-${isoLikeDate[3]}`
  const parsed = new Date(raw.replace(' ', 'T'))
  if (Number.isNaN(parsed.getTime())) return null
  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`
}

export const normalizeFixtureCountryKey = value =>
  String(value || '').trim().toLowerCase()

export const splitScheduleLabel = label =>
  String(label || '').split('\n').map(part => part.trim()).filter(Boolean)
export const getMatchLink = matchId =>
  Number.isFinite(Number(matchId))
    ? `https://hattrick.org/goto.ashx?path=/Club/Matches/Match.aspx?matchID=${Number(matchId)}`
    : null
const worldCupRoundOffsets = { 1: 0, 2: 10, 3: 16, 4: 19, 5: 22 }

export const getScheduleMatchRound = (label, tournamentKind) => {
  const kind = String(tournamentKind || '').toUpperCase()
  const text = String(label || '')
  if (kind === 'WC') {
    const match = text.match(/WC krs (\d+) Ottelu (\d+)/i)
    if (!match) return null
    const round = Number(match[1])
    const matchNumber = Number(match[2])
    return Number.isFinite(worldCupRoundOffsets[round])
      ? worldCupRoundOffsets[round] + matchNumber
      : null
  }
  const match = text.match(new RegExp(`${kind} Ottelu (\\d+)`, 'i'))
  return match ? Number(match[1]) || null : null
}

export const getRoundBucketFromUtils = (tournamentKind, matchRound) => {
  const kind = String(tournamentKind || '').trim().toUpperCase()
  const round = Number(matchRound)
  if (!kind || !Number.isFinite(round) || round <= 0) return null
  if (kind === 'WC' || kind === 'NC') {
    if (round <= 10) return 'R1'
    if (round <= 16) return 'R2'
    if (round <= 19) return 'R3'
    if (round <= 22) return 'R4'
    if (round <= 25) return 'R5'
    return 'Pudotuspelit'
  }
  if (kind === 'CC' || kind === 'CL') return round <= 10 ? 'Alkuerät' : 'Pudotuspelit'
  return null
}

export const getCalendarCellLabel = (calendarPosition, tournamentKind) => {
  const seasonData = worldCupCalendar.find(
    season => Number(season.season) === Number(calendarPosition?.part)
  )
  const weekData = seasonData?.weeks?.find(
    week => Number(week.week) === Number(calendarPosition?.week)
  )
  const dayIndex = Number(calendarPosition?.day) - 1
  const cell = weekData?.cells?.[dayIndex]

  if (!cell || cell === '-') return null

  return String(cell)
}

export const extractMatchNumberFromCalendarLabel = (label, tournamentKind) => {
  const kind = String(tournamentKind || '')
    .trim()
    .toUpperCase()
  const lines = String(label || '')
    .split('\n')
    .map(part => part.trim())
    .filter(Boolean)

  if (lines.length === 0) return null

  const kindIndex = lines.findIndex(line => line.startsWith(kind))
  const candidateLine =
    kindIndex >= 0
      ? lines[kindIndex + 1] || ''
      : lines.find(line => /Ottelu/i.test(line)) || ''

  const match = candidateLine.match(/Ottelu\s+(\d+)/i)
  return match ? Number(match[1]) || null : null
}

export const matchesCompetitionTargets = (competition, targets) => {
  if (!targets || targets.length === 0) return true
  return targets.includes(competition)
}

export const shouldShowSchedule = (label, selection) => {
  const normalizedLabel = String(label || '')

  if (normalizedLabel.includes('WC') || normalizedLabel.includes('NC')) {
    return selection === 'WC' || selection === 'NC'
  }

  if (normalizedLabel.includes('CC') || normalizedLabel.includes('CL')) {
    return selection === 'CC' || selection === 'CL'
  }

  return true
}

export const getScheduleLabel = (label, selection) => {
  const parts = splitScheduleLabel(label)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0]

  if (parts.length >= 4) {
    if (selection === 'WC') return `${parts[0]} ${parts[1]}`
    if (selection === 'NC') return `${parts[2]} ${parts[3]}`
    if (selection === 'CC') return `${parts[0]} ${parts[1]}`
    if (selection === 'CL') return `${parts[2]} ${parts[3]}`
  }

  return selection === 'CL' || selection === 'NC'
    ? parts[1] || parts[0]
    : parts[0]
}

export const shouldShowCalendarReset = ({
  season,
  week,
  nationalCompetition,
  worldCompetition,
}) => {
  if (season === 1 && week === 11) {
    return nationalCompetition === 'CL'
  }

  if (season === 1 && week === 13) {
    const nationalCode = nationalCompetition
    return nationalCode === 'CC' || nationalCode === 'CL'
  }

  if (season === 2 && [6, 10, 12].includes(week)) {
    return worldCompetition === 'WC'
  }

  return true
}

export const compactMatchLabel = label =>
  String(label || '')
    .replace(/\bOttelu\s+(\d+)\b/gi, 'O$1')
    .replace(/\bCC\s+Puolivälierät\b/gi, '½välierät')
    .replace(/\s*\/\s*(NC|CL|WC|CC)\s+O\d+\b.*$/i, '')
    .replace(/\s*\/\s*(NC|CL|WC|CC)\b.*$/i, '')

export const getRoundBucketOrder = (tournamentKind, bucket) => {
  const kind = String(tournamentKind || '')
    .trim()
    .toUpperCase()
  const normalizedBucket = String(bucket || '').trim()

  if (!kind || !normalizedBucket) return null

  if (kind === 'WC' || kind === 'NC') {
    return (
      {
        R1: 1,
        R2: 2,
        R3: 3,
        R4: 4,
        R5: 5,
        Pudotuspelit: 6,
      }[normalizedBucket] || null
    )
  }

  if (kind === 'CC' || kind === 'CL') {
    return (
      {
        Alkuerät: 1,
        Pudotuspelit: 2,
      }[normalizedBucket] || null
    )
  }

  return null
}

export const getRoundBucketFromCalendarPosition = (
  calendarPosition,
  tournamentKind
) => {
  const kind = String(tournamentKind || '')
    .trim()
    .toUpperCase()
  const part = Number(calendarPosition?.part)
  const week = Number(calendarPosition?.week)
  const dayIndex = Number(calendarPosition?.day)

  if (
    !kind ||
    !Number.isFinite(part) ||
    !Number.isFinite(week) ||
    !Number.isFinite(dayIndex)
  ) {
    return null
  }

  const slot = calendarSlotIndex({
    part,
    week,
    day: dayLabels[dayIndex - 1],
  })

  const windows =
    kind === 'WC' || kind === 'NC'
      ? [
          {
            bucket: 'R1',
            endSlot: calendarSlotIndex({ part: 2, week: 5, day: 'Pe' }),
          },
          {
            bucket: 'R2',
            endSlot: calendarSlotIndex({ part: 2, week: 9, day: 'Pe' }),
          },
          {
            bucket: 'R3',
            endSlot: calendarSlotIndex({ part: 2, week: 11, day: 'Pe' }),
          },
          {
            bucket: 'R4',
            endSlot: calendarSlotIndex({ part: 2, week: 13, day: 'Pe' }),
          },
          {
            bucket: 'R5',
            endSlot: calendarSlotIndex({ part: 2, week: 15, day: 'Pe' }),
          },
          {
            bucket: 'Pudotuspelit',
            endSlot: calendarSlotIndex({ part: 2, week: 16, day: 'Pe' }),
          },
        ]
      : kind === 'CC' || kind === 'CL'
      ? [
          {
            bucket: 'Alkuerät',
            endSlot: calendarSlotIndex({ part: 1, week: 10, day: 'Pe' }),
          },
          {
            bucket: 'Pudotuspelit',
            endSlot: calendarSlotIndex({ part: 1, week: 13, day: 'Pe' }),
          },
        ]
      : []

  return windows.find(window => slot <= window.endSlot)?.bucket || null
}

export const formatTournamentStatusLabel = group => {
  const kind = String(group?.tournamentKind || '').trim()
  if (!kind) return '—'

  const friendlyNames = {
    CC: 'Continents Cup',
    CL: 'Continents League',
    WC: 'World Cup',
    NC: 'Nations Cup',
  }

  return `${kind} (${friendlyNames[kind] || group?.tournamentName || kind})`
}

export const getActiveNationalTeamName = (user, nationalTeamId = null) => {
  const activeNationalTeamId = Number(nationalTeamId || user?.activeNationalTeam)
  const activeStaffEntry = Array.isArray(user?.staff)
    ? user.staff.find(
        staff => Number(staff?.nationalTeamId) === activeNationalTeamId
      )
    : null

  return activeStaffEntry?.nationalTeamName || null
}

const dayIndexMap = {
  Ma: 0,
  Ti: 1,
  Ke: 2,
  To: 3,
  Pe: 4,
  La: 5,
  Su: 6,
}

const calendarSlotIndex = ({ part, week, day }) =>
  (part - 1) * 16 * 7 + (week - 1) * 7 + dayIndexMap[day]

export const createInitialMatchRows = (opponentCountries = defaultOpponentCountries) =>
  createDefaultMatchRows(externalMatchSchedule.length).map((row, index) => ({
    ...row,
    midfieldAnchor: false,
    schedule: externalMatchSchedule[index] || null,
    countryValues: normalizeCountryValues({}, opponentCountries),
  }))
